import { useState } from 'react';
import { Checkbox, List, ListItemText, Stack, Typography } from '@mui/material';
import { getAppStoreById, requestAddNewChannelForAppStore } from '@/api/appstore.api';
import { useAppStoreStore } from '@/stores/appstore.store';
import { useIndicatorStore } from '@/stores/indicator.store';
import { useToastStore } from '@/stores/toast.store';
import { setPreventDisplyForWeek } from '@/utils/modal.util';
import FlexBox from '@/components/FlexBox';
import BaseModal from '@/components/modal/BaseModal';

export const ALLOW_GOOGLE_MODAL_STORAGE = 'ALLOW_GOOGLE_MODAL_STORAGE';

const GoogleChannelRequestModal = ({
  showModal,
  handleShowModal,
  handleShowButton,
  updateCheckStatus,
  appStoreId
}: {
  showModal: boolean;
  handleShowModal: () => void;
  handleShowButton: () => void;
  updateCheckStatus: () => void;
  appStoreId: number;
}) => {
  const guides = [
    {
      title: '리뷰 수집 및 긍/부정 분석',
      desc: '네이버맵 등과 같이 한눈에 확인하실 수 있습니다.'
    },
    {
      title: '자동 답변',
      desc: '추후 곧 업데이트로 제공될 예정입니다. 조금만 더 기다려 주세요.'
    },
    {
      title: '삭제요청',
      desc: '구글의 정책상 보다 복잡한 절차를 요구하여, 제휴 로펌의 전문적인 지원을 통해 진행되고 있습니다. (유료)'
    }
  ];

  const [notShowModalForWeek, setNotShowModalForWeek] = useState(false);

  const { setShowIndicator } = useIndicatorStore((state) => state);
  const { setShowToast, setToastInfos } = useToastStore((state) => state);
  const { updateAppStores } = useAppStoreStore((state) => state);

  return (
    <BaseModal
      showModal={showModal}
      handleShowModal={handleShowModal}
      modalTitle="구글맵 리뷰 추가 안내"
      onCloseModal={async () => {
        if (notShowModalForWeek) {
          setPreventDisplyForWeek(`${ALLOW_GOOGLE_MODAL_STORAGE}_${appStoreId}`, !notShowModalForWeek);
        }
      }}
      buttons={{
        primary: {
          title: '구글맵 리뷰 추가하기',
          onClick: async () => {
            setShowIndicator(true);

            const res = await requestAddNewChannelForAppStore({
              appStoreId,
              channelId: 'GOOGLE_MAP'
            });
            if (res) {
              const { isSuccess, errors } = res;
              if (isSuccess) {
                const googleMapAddedAppStore = await getAppStoreById(appStoreId);
                if (googleMapAddedAppStore) {
                  updateAppStores(googleMapAddedAppStore);
                }
                setToastInfos({
                  text: '구글맵 리뷰 수집 요청이 성공 했습니다.\n추가 완료 시 알려드리겠습니다.'
                });

                updateCheckStatus();
              } else if (errors) {
                const isAppStoreNotFound = errors.findIndex((_error) => _error.code === 'REQUEST-002') > -1;
                const isFaliToSaveAppStore = errors.findIndex((_error) => _error.code === 'SERVER-002') > -1;
                if (isAppStoreNotFound) {
                  setToastInfos({
                    text: '현재 병원을 구글맵에서 찾을 수 없습니다.\n만약 병원이 이미 등록되어 있다면, 고객센터로 문의해주세요.'
                  });
                } else if (isFaliToSaveAppStore) {
                  setToastInfos({
                    text: '해당 병원에 구글맵 리뷰를 추가하지 못했습니다.\n문제가 지속될 경우 고객센터로 문의해 주세요.'
                  });
                } else {
                  setToastInfos({
                    text: '지금 처리할 수 없습니다.\n잠시 후에 다시 시도해 주시고, 문제가 지속될 경우 고객센터로 문의해 주세요.'
                  });
                }
              }

              setShowIndicator(false);
              setShowToast(true);
              handleShowButton();
              handleShowModal();
            }
          }
        }
      }}
    >
      <Stack gap={'12px'} direction={'column'}>
        <Typography sx={{ fontSize: '14px', lineHeight: '20px' }} color="textSecondary">
          이제 <strong>리뷰웍스에서 쉽게 구글맵 리뷰를 확인</strong>하실 수 있습니다. 구글 리뷰 추가 하기 버튼을 클릭하시면 수집과 분석이 시작됩니다.
        </Typography>
        <Typography sx={{ fontWeight: 600, lineHeight: '24px', fontSize: '16px' }}>업데이트 내용 안내</Typography>
        <List sx={{ listStyleType: 'disc', paddingY: 0, paddingLeft: '16px' }}>
          {guides.map((guide, i) => (
            <ListItemText key={i} sx={{ display: 'list-item' }}>
              <Typography color="textSecondary">
                <strong>{guide.title}</strong> : {guide.desc}
              </Typography>
            </ListItemText>
          ))}
        </List>
        <Typography sx={{ fontSize: '14px', lineHeight: '20px' }} color="textSecondary">
          추가로 필요하거나 개선되었으면 하는 점이 있다면 언제든 말씀해주세요. 여러분의 소중한 의견을 바탕으로 더 나은 서비스를 만들어가겠습니다.
        </Typography>
        <FlexBox style={{ justifyContent: 'left' }}>
          <Checkbox
            checked={notShowModalForWeek}
            color="success"
            sx={{ paddingLeft: 0 }}
            onChange={(e) => {
              const isChecked = (e.currentTarget as HTMLInputElement)?.checked;
              setNotShowModalForWeek(isChecked);
            }}
          />
          <Typography>일주일간 보지 않기</Typography>
        </FlexBox>
      </Stack>
    </BaseModal>
  );
};

export default GoogleChannelRequestModal;
